import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Table } from '../../components/table/table';
import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import {
  TableHeadSelectionComponent,
  TableRowSelectionComponent,
} from '../../components/select-column/select-column';
import { AdminService } from '../../services/admin-service';
import { Button } from '../../components/button/button';
import { Modal } from '../../components/modal/modal';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputComponent } from '../../components/input/input';
import { Select } from '../../components/select/select';
import { MultiSelect } from '../../components/multi-select/multi-select';
import { TableButtons } from '../../components/table-buttons/table-buttons';
import { ConfirmDialogService } from '../../services/confirm-dialog-service';
import { ToastService } from '../../services/toast-service';
import { ConfirmDialog } from '../../components/confirm-dialog/confirm-dialog';
import { BackButton } from '../../components/back-button/back-button';
import { Loading } from '../../components/loading/loading';

type TUser = {
  userId: string;
  name: string;
  usename: string;
  email: string;
};

@Component({
  selector: 'app-users',
  imports: [
    Table,
    Button,
    Modal,
    ReactiveFormsModule,
    InputComponent,
    Select,
    MultiSelect,
    ConfirmDialog,
    BackButton,
    RouterLink,
    Loading,
  ],
  templateUrl: './users.html',
  styleUrl: './users.scss',
})
export class Users implements OnInit {
  loading = false;
  showModal = false;
  showConfirmDialog = false;
  message = '';

  clientId = signal('');
  clientLabel = signal('');
  merchantId = signal('');
  private activatedRoute = inject(ActivatedRoute);

  columns: ColumnDef<TUser>[] = [
    {
      id: 'select-col',
      header: () => flexRenderComponent(TableHeadSelectionComponent),
      cell: () => flexRenderComponent(TableRowSelectionComponent),
    },
    {
      accessorKey: 'name',
      header: 'Nome',
    },
    {
      accessorKey: 'usename',
      header: 'Username',
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: (info) => `<i>${info.getValue<string>()}</i>`,
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const user = row.original;
        return flexRenderComponent(TableButtons, {
          inputs: {
            title: 'trash',
          },
          outputs: {
            clickEvent: () => this.deletarUser(user),
          },
        });
      },
    },
  ];
  data: any[] = [];

  userRegister = new FormGroup({
    name: new FormControl(''),
    email: new FormControl(''),
    password: new FormControl(''),
    role: new FormControl(0),
    requiredActions: new FormControl<number[]>([]),
    clientId: new FormControl(''),
    merchantId: new FormControl(''),
  });

  optionsRole = [
    { label: 'Lojista', value: 0 },
    { label: 'Cliente', value: 2 },
    { label: 'Administrador', value: 4 },
  ];

  merchantOptions: any[] = [];

  multiSelectOptions = [
    { label: 'Verificar email', value: 0 },
    { label: 'Configurar OTP', value: 1 },
    { label: 'Registrar auth', value: 3 },
  ];

  constructor(
    private adminService: AdminService,
    private confirmDialogService: ConfirmDialogService,
    private toast: ToastService
  ) {
    this.confirmDialogService.showDialog$.subscribe(({ message }) => {
      this.message = message;
      this.showConfirmDialog = true;
    });

    this.activatedRoute.params.subscribe((params) => {
      this.clientId.set(params['id']);
      this.clientLabel.set(params['label']);
    });
  }

  ngOnInit(): void {
    this.getUsers();

    this.adminService.getMerchants(this.clientId()).subscribe((resp) => {
      this.merchantOptions = resp.map((m) => ({ label: m.merchantLabel, value: m.merchantId }));
    });
  }

  openModal() {
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
  }

  onConfirm() {
    this.confirmDialogService.confirm();
    this.showConfirmDialog = false;
  }

  onCancel() {
    this.confirmDialogService.cancel();
    this.showConfirmDialog = false;
  }

  salvar() {
    this.userRegister.patchValue({
      clientId: this.clientId(),
      role: Number(this.userRegister.value.role),
    });
    if (this.userRegister.value.role != 0) {
      this.userRegister.patchValue({
        merchantId: '',
      });
    }
    this.adminService.registerUser(this.userRegister.value).subscribe((resp) => {
      this.getUsers();
      this.userRegister.reset();
      this.closeModal();
    });
  }

  deletarUser(user: TUser) {
    this.confirmDialogService
      .requestConfirmation(`Deseja deletar [${user.usename}]?`)
      .subscribe((confirmed) => {
        if (confirmed) {
          this.adminService.deleteUser(user.userId).subscribe(() => {
            this.toast.show({
              message: 'Usuário deletado com sucesso',
              type: 'info',
            });

            this.getUsers();
          });
        } else {
          console.log('Ação cancelada');
        }
      });
  }

  async getUsers() {
    this.loading = true;

    this.adminService.getUsers(this.clientId()).subscribe({
      next: (resp) => {
        this.data = resp;
      },
      error: () => {
        this.data = [];
      },
    });
    this.loading = false;
  }
}
